import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Wrench, Settings2, Trash2, Edit3, Search, CalendarClock, Refrigerator, WashingMachine, AirVent, Layers3, ChevronDown, MoreHorizontal, Unlink } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { getApiUrl } from "../config/api";
import authFetch from '../utils/authFetch';
import LoadingSpinner from "../components/ui/LoadingSpinner";

// Icon per category (simple mapping; expand as needed)
const CategoryIcon = {
  Dishwasher: Wrench,
  Refrigerator: Refrigerator,
  Washer: WashingMachine,
  Dryer: WashingMachine,
  HVAC: AirVent,
  Other: Layers3,
};

// --- Helpers
const fmt = (d) => (d ? d.toLocaleDateString() : "—");

// Sentinel value for the "Unassigned" option in the property dropdown. Radix
// Select can't use an empty-string value, so we use a non-conflicting marker.
const UNASSIGNED = "__unassigned__";

// --- Component
export default function MaintenanceWireframe() {
  const [properties, setProperties] = useState([]);
  const [appliances, setAppliances] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortKey, setSortKey] = useState("property");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Fetch appliances and properties on mount
  useEffect(() => {
    fetchAppliances();
  }, []);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchAppliances = async () => {
    try {
      setLoading(true);
      setError(null);
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        setError('User not logged in');
        return;
      }

      const response = await authFetch(getApiUrl('/api/v1/appliance/list'));

      if (response.ok) {
        const data = await response.json();

        // Transform API response to match frontend structure
        const propsData = data.properties.map(prop => ({
          id: prop.property_id.toString(),
          name: prop.property_name,
          address: prop.property_address,
        }));

        const mapAppliance = (app, propertyId) => ({
          id: app.id.toString(),
          propertyId: propertyId != null ? propertyId.toString() : "",
          category: app.category,
          description: app.description,
          make: app.make,
          model: app.model,
          serial: app.serial,
          installedOn: app.installation_date ? new Date(app.installation_date) : null,
          lastServicedOn: app.last_serviced ? new Date(app.last_serviced) : null,
        });

        const appliancesData = [];
        data.properties.forEach(prop => {
          prop.appliances.forEach(app => {
            appliancesData.push(mapAppliance(app, prop.property_id));
          });
        });

        const unassignedData = (data.unassigned || []).map(app => mapAppliance(app, null));

        setProperties(propsData);
        setAppliances(appliancesData);
        setUnassigned(unassignedData);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch appliances');
      }
    } catch (err) {
      console.error('Error fetching appliances:', err);
      setError('An error occurred while fetching appliances');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let rows = appliances.filter(a => {
      const matchesQuery = [a.category, a.description, a.make, a.model, a.serial]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query.toLowerCase());
      const matchesProperty = propertyFilter === "all" || a.propertyId === propertyFilter;
      const matchesCategory = categoryFilter === "all" || a.category === categoryFilter;
      return matchesQuery && matchesProperty && matchesCategory;
    });

    rows = rows.sort((a,b) => {
      if (sortKey === "property") return a.propertyId.localeCompare(b.propertyId);
      if (sortKey === "category") return a.category.localeCompare(b.category);
      if (sortKey === "installed") return (a.installedOn?.getTime() || 0) - (b.installedOn?.getTime() || 0);
      return 0;
    });

    return rows;
  }, [appliances, query, propertyFilter, categoryFilter, sortKey]);

  const grouped = useMemo(() => {
    const map = {};
    for (const a of filtered) {
      map[a.propertyId] ||= [];
      map[a.propertyId].push(a);
    }
    return map;
  }, [filtered]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appliance? This action cannot be undone.')) return;
    try {
      const response = await authFetch(getApiUrl(`/api/v1/appliance/delete/${id}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove from local state (could be in either grouping)
        setAppliances(prev => prev.filter(a => a.id !== id));
        setUnassigned(prev => prev.filter(a => a.id !== id));
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete appliance');
      }
    } catch (err) {
      console.error('Error deleting appliance:', err);
      setError('An error occurred while deleting appliance');
    }
  };

  const handleUnassign = async (a) => {
    if (!window.confirm('Remove this appliance from the property? It will move to the unassigned list.')) return;
    // Pass the existing field values through so detaching from the property
    // doesn't clear the appliance's other details.
    await upsertAppliance({
      id: a.id,
      category: a.category,
      description: a.description,
      make: a.make,
      model: a.model,
      installedOn: a.installedOn || null,
      lastServicedOn: a.lastServicedOn || null,
      unassign: true,
    });
  };

  const upsertAppliance = async (payload) => {
    try {
      if (payload.id) {
        const updateData = {
          description: payload.description?.trim() || null,
          category: payload.category,
          make: payload.make?.trim() || null,
          model: payload.model?.trim() || null,
          installation_date: payload.installedOn ? payload.installedOn.toISOString() : null,
          last_serviced: payload.lastServicedOn ? payload.lastServicedOn.toISOString() : null,
        };

        // Sending property_id null unassigns the appliance, moving it back to
        // the unassigned list. Otherwise, including property_id assigns (or
        // reassigns) the appliance to a property — this is how an unassigned,
        // SMS-created appliance gets attached to a property.
        if (payload.unassign) {
          updateData.property_id = null;
        } else if (payload.propertyId) {
          updateData.property_id = parseInt(payload.propertyId);
        }

        const response = await authFetch(getApiUrl(`/api/v1/appliance/update/${payload.id}`), {
          method: 'PUT',
          body: JSON.stringify(updateData),
        });

        if (response.ok) {
          // Refetch so an appliance that moved out of the unassigned list (or
          // between properties) is reflected in both groupings.
          await fetchAppliances();
          return true;
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to update appliance');
          return false;
        }
      } else {
        const createData = {
          property_id: parseInt(payload.propertyId),
          description: payload.description?.trim() || null,
          category: payload.category,
          make: payload.make?.trim() || null,
          model: payload.model?.trim() || null,
          installation_date: payload.installedOn ? payload.installedOn.toISOString() : null,
          last_serviced: payload.lastServicedOn ? payload.lastServicedOn.toISOString() : null,
        };

        const response = await authFetch(getApiUrl('/api/v1/appliance/create'), {
          method: 'POST',
          body: JSON.stringify(createData),
        });

        if (response.ok) {
          await fetchAppliances();
          return true;
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to create appliance');
          return false;
        }
      }
    } catch (err) {
      console.error('Error saving appliance:', err);
      setError('An error occurred while saving appliance');
      return false;
    }
  };

  const categories = ["Dishwasher", "Refrigerator", "Washer", "Dryer", "HVAC", "Other"];

  return (
    <Layout pageTitle="Maintenance & AI Diagnostics">
      <div className="w-full bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Maintenance</h1>
            <p className="text-sm text-gray-600">Track appliances & equipment by property, add new items, and keep service history current.</p>
          </div>
        <div className="flex gap-2">
          <Button className="rounded-full text-sm" variant="secondary" onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth'})}>
            <Settings2 className="mr-1.5 h-4 w-4"/>View All Properties
          </Button>
          <Button className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm" onClick={openCreate} disabled={loading || properties.length === 0}>
            <Plus className="mr-1.5 h-4 w-4"/>Add Appliance
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <>

      {/* Filters */}
      <Card className="mt-6 shadow-sm bg-white">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 h-10">
              <Search className="h-4 w-4 shrink-0 text-gray-500"/>
              <Input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Search…" className="border-0 focus-visible:ring-0 text-sm text-gray-900 placeholder:text-gray-400 h-auto p-0"/>
            </div>
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10"><SelectValue placeholder="Property"/></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All properties</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10"><SelectValue placeholder="Category"/></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10"><SelectValue placeholder="Sort by"/></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="property">Sort: Property</SelectItem>
                <SelectItem value="category">Sort: Category</SelectItem>
                <SelectItem value="installed">Sort: Install date</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Unassigned appliances (e.g. created via inbound SMS) */}
      {unassigned.length > 0 && (
        <Card className="mt-6 border border-amber-200 shadow-sm bg-amber-50/40">
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg text-gray-900">Unassigned</CardTitle>
              <div className="text-xs text-gray-600">{unassigned.length} appliance{unassigned.length !== 1 ? 's' : ''} waiting to be assigned to a property</div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-y-2">
                <thead>
                  <tr className="text-left text-xs text-gray-600 uppercase tracking-wide">
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Details</th>
                    <th className="px-3 py-2 w-40"></th>
                  </tr>
                </thead>
                <tbody>
                  {unassigned.map((a) => {
                    const Icon = CategoryIcon[a.category] || Layers3;
                    return (
                      <tr key={a.id} onClick={() => openEdit(a)} className="bg-white rounded-xl shadow-sm align-top cursor-pointer transition-colors hover:bg-gray-50">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-300 bg-gray-50"><Icon className="h-4 w-4 text-gray-700"/></span>
                            <div>
                              <div className="font-medium text-gray-900">{a.category}</div>
                              <div className="text-xs text-gray-500">#{a.id.slice(-5)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="space-y-1">
                            <div className="text-sm text-gray-900">{a.description || <span className="text-gray-500">No description</span>}</div>
                            <div className="text-xs text-gray-600">{[a.make, a.model].filter(Boolean).join(" · ") || "—"}</div>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="secondary" className="rounded-full text-xs" onClick={() => openEdit(a)} disabled={properties.length === 0}>
                              Assign to property
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="rounded-lg h-8 w-8"><MoreHorizontal className="h-4 w-4 text-gray-600"/></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white">
                                <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => handleDelete(a.id)}>
                                  <Trash2 className="mr-2 h-3.5 w-3.5"/> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grouped list */}
      <div className="mt-6 space-y-4">
        {properties.map((prop) => {
          const rows = grouped[prop.id] || [];
          return (
            <motion.div key={prop.id} layout>
              <Card className="border shadow-sm bg-white">
                <CardHeader className="flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-gray-900">{prop.name} <span className="text-gray-600 font-normal">— {prop.address}</span></CardTitle>
                    <div className="text-xs text-gray-600">{rows.length} appliance{rows.length !== 1 ? 's' : ''}</div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {rows.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">No appliances yet. Click <span className="font-medium text-gray-900">Add Appliance</span> to create one.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-separate border-spacing-y-2">
                        <thead>
                          <tr className="text-left text-xs text-gray-600 uppercase tracking-wide">
                            <th className="px-3 py-2">Category</th>
                            <th className="px-3 py-2">Details</th>
                            <th className="px-3 py-2">Install</th>
                            <th className="px-3 py-2">Last serviced</th>
                            <th className="px-3 py-2 w-12"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((a) => {
                            const Icon = CategoryIcon[a.category] || Layers3;
                            return (
                              <tr key={a.id} onClick={() => openEdit(a)} className="bg-white rounded-xl shadow-sm align-top cursor-pointer transition-colors hover:bg-gray-50">
                                <td className="px-3 py-3">
                                  <div className="flex items-center gap-3">
                                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-300 bg-gray-50"><Icon className="h-4 w-4 text-gray-700"/></span>
                                    <div>
                                      <div className="font-medium text-gray-900">{a.category}</div>
                                      <div className="text-xs text-gray-500">#{a.id.slice(-5)}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-3">
                                  <div className="space-y-1">
                                    <div className="text-sm text-gray-900">{a.description || <span className="text-gray-500">No description</span>}</div>
                                    <div className="text-xs text-gray-600">{[a.make, a.model, a.serial && `SN ${a.serial}`].filter(Boolean).join(" · ") || "—"}</div>
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-sm text-gray-900">{fmt(a.installedOn)}</td>
                                <td className="px-3 py-3 text-sm">
                                  <div className="flex items-center gap-2 text-gray-900">
                                    <CalendarClock className="h-4 w-4 text-gray-600"/>
                                    <span>{fmt(a.lastServicedOn)}</span>
                                  </div>
                                </td>
                                <td className="px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="rounded-lg h-8 w-8"><MoreHorizontal className="h-4 w-4 text-gray-600"/></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="bg-white">
                                      <DropdownMenuItem onClick={() => openEdit(a)}>
                                        <Edit3 className="mr-2 h-3.5 w-3.5"/> Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleUnassign(a)}>
                                        <Unlink className="mr-2 h-3.5 w-3.5"/> Unassign
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => handleDelete(a.id)}>
                                        <Trash2 className="mr-2 h-3.5 w-3.5"/> Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

        {/* Create / Edit Dialog */}
        <ApplianceDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          properties={properties}
          categories={categories}
          editing={editing}
          onSubmit={async (payload) => {
            const ok = await upsertAppliance(payload);
            if (ok) setDialogOpen(false);
          }}
        />
        </>
      )}
      </div>
    </Layout>
  );
}

function ApplianceDialog({ open, onOpenChange, properties, categories, editing, onSubmit }) {
  // When editing an existing appliance with no property, show "Unassigned" as
  // the current selection; new appliances default to the first property.
  const initialPropertyId = editing
    ? (editing.propertyId || UNASSIGNED)
    : (properties[0]?.id || "");

  const [propertyId, setPropertyId] = useState(initialPropertyId);
  const [category, setCategory] = useState(editing?.category || categories[0]);
  const [description, setDescription] = useState(editing?.description || "");
  const [make, setMake] = useState(editing?.make || "");
  const [model, setModel] = useState(editing?.model || "");
  const [installedOn, setInstalledOn] = useState(editing?.installedOn || null);
  const [lastServicedOn, setLastServicedOn] = useState(editing?.lastServicedOn || null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setPropertyId(editing ? (editing.propertyId || UNASSIGNED) : (properties[0]?.id || ""));
    setCategory(editing?.category || categories[0]);
    setDescription(editing?.description || "");
    setMake(editing?.make || "");
    setModel(editing?.model || "");
    setInstalledOn(editing?.installedOn || null);
    setLastServicedOn(editing?.lastServicedOn || null);
    setSaving(false);
  }, [open, editing, properties, categories]);

  const save = async () => {
    if (!propertyId || !category || saving) return;
    setSaving(true);
    try {
      // Selecting "Unassigned" detaches the appliance from any property.
      const base = { id: editing?.id, category, description, make, model, installedOn: installedOn || null, lastServicedOn: lastServicedOn || null };
      const payload = propertyId === UNASSIGNED ? { ...base, unassign: true } : { ...base, propertyId };
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">{editing ? "Edit appliance" : "Add appliance"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="space-y-2">
            <Label className="text-gray-900 text-sm">Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10"><SelectValue placeholder="Select a property"/></SelectTrigger>
              <SelectContent className="bg-white">
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                {editing && <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-900 text-sm">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10"><SelectValue placeholder="Select a category"/></SelectTrigger>
              <SelectContent className="bg-white">
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-gray-900 text-sm">Description</Label>
            <Input value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="e.g., Kitchen dishwasher" className="rounded-xl bg-white border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 h-10"/>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-900 text-sm">Make</Label>
            <Input value={make} onChange={(e)=>setMake(e.target.value)} placeholder="e.g., GE, Whirlpool" className="rounded-xl bg-white border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 h-10"/>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-900 text-sm">Model</Label>
            <Input value={model} onChange={(e)=>setModel(e.target.value)} placeholder="Model number" className="rounded-xl bg-white border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 h-10"/>
          </div>
          <DatePicker label="Installation date (optional)" value={installedOn} onChange={setInstalledOn} />
          <DatePicker label="Last serviced (optional)" value={lastServicedOn} onChange={setLastServicedOn} />
        </div>
        <DialogFooter>
          <Button variant="secondary" className="rounded-full text-sm" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm disabled:opacity-50 disabled:cursor-not-allowed" onClick={save} disabled={saving}>{saving ? "Saving…" : editing ? "Save changes" : "Add appliance"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DatePicker({ label, value, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="text-gray-900 text-sm">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between rounded-xl bg-white border-gray-300 text-sm text-gray-900 hover:bg-gray-50 h-10">
            <span className="text-gray-900">{value ? value.toLocaleDateString() : "mm/dd/yyyy"}</span>
            <ChevronDown className="h-3.5 w-3.5 text-gray-600"/>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 bg-white" align="start">
          <Calendar mode="single" selected={value ?? undefined} onSelect={(d) => onChange(d ?? null)} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}
